import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { processPayroll, getPayrollRuns } from '../../../services/payrollService';

function parseConnectTeamXLSX(file, source) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (raw.length < 2) { resolve([]); return; }

        const headers = raw[0];
        const firstNameIdx = headers.findIndex(h => String(h).toLowerCase().includes('first'));
        const lastNameIdx = headers.findIndex(h => String(h).toLowerCase().includes('last'));
        const totalWorkIdx = headers.findIndex(h => String(h).toLowerCase().includes('total work'));
        const totalRegIdx = headers.findIndex(h => String(h).toLowerCase().includes('total regular'));
        const totalOTIdx = headers.findIndex(h => String(h).toLowerCase().includes('total overtime'));
        const totalPTOIdx = headers.findIndex(h => String(h).toLowerCase().includes('total paid time off'));
        const typeIdx = headers.findIndex(h => String(h).toLowerCase() === 'type');

        const employees = [];
        let currentEmployee = null;

        for (let i = 1; i < raw.length; i++) {
          const row = raw[i];
          const firstName = String(row[firstNameIdx] || '').trim();
          const lastName = String(row[lastNameIdx] || '').trim();

          if (firstName && lastName) {
            // New employee row
            if (currentEmployee) employees.push(currentEmployee);
            currentEmployee = {
              firstName,
              lastName,
              source,
              totalWorkHours: parseFloat(row[totalWorkIdx]) || 0,
              totalRegular: parseFloat(row[totalRegIdx]) || 0,
              totalOvertime: parseFloat(row[totalOTIdx]) || 0,
              totalPTO: parseFloat(row[totalPTOIdx]) || 0,
              jobTypes: row[typeIdx] ? [String(row[typeIdx]).trim()] : [],
            };
          } else if (currentEmployee && row[typeIdx]) {
            // Additional shift rows for same employee
            currentEmployee.jobTypes.push(String(row[typeIdx]).trim());
          }
        }
        if (currentEmployee) employees.push(currentEmployee);

        resolve(employees.filter(e => e.totalWorkHours > 0 || e.totalPTO > 0));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

export default function PayrollProcess() {
  const [bakeryFile, setBakeryFile] = useState(null);
  const [ccFile, setCCFile] = useState(null);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processedBy, setProcessedBy] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [runHistory, setRunHistory] = useState([]);

  React.useEffect(() => { loadRunHistory(); }, []);

  async function loadRunHistory() {
    const res = await getPayrollRuns();
    if (res.success) setRunHistory(res.data.sort((a, b) => new Date(b.processedDate) - new Date(a.processedDate)));
  }

  async function handleProcess() {
    if (!bakeryFile && !ccFile) return setError('Please upload at least one timesheet file.');
    if (!periodStart || !periodEnd) return setError('Please enter the pay period dates.');
    setError('');
    setProcessing(true);

    try {
      const bakeryData = bakeryFile ? await parseConnectTeamXLSX(bakeryFile, 'bakery') : [];
      const ccData = ccFile ? await parseConnectTeamXLSX(ccFile, 'cc') : [];

      const res = await processPayroll({
        periodStart,
        periodEnd,
        bakeryData,
        ccData,
        processedBy: processedBy || 'Unknown',
      });

      if (res.success) setResults(res);
      else setError('Error: ' + res.error);
    } catch (e) {
      setError('Error parsing file: ' + e.message);
    }
    setProcessing(false);
  }

  function handleExport() {
    if (!results) return;
    const wb = XLSX.utils.book_new();

    // AR employees
    const arRows = results.results.filter(r => r.department === 'AR' || r.department === 'Both');
    const ccRows = results.results.filter(r => r.department === 'CC' || r.department === 'Both');

    const toSheetData = (rows) => [
      ['Name', 'Hours', 'Overtime Hours', 'ADP Amount', 'Cash Amount', 'PTO Hours', 'Pay Type', 'Notes'],
      ...rows.map(r => [
        r.firstName + ' ' + r.lastName,
        parseFloat(r.adpHours) || '',
        parseFloat(r.cashHours) || '',
        parseFloat(r.adpAmount) || '',
        parseFloat(r.cashAmount) || '',
        parseFloat(r.ptoHours) || '',
        r.payType,
        r.notes
      ])
    ];

    const arWS = XLSX.utils.aoa_to_sheet(toSheetData(arRows));
    XLSX.utils.book_append_sheet(wb, arWS, 'AR Payroll');

    const ccWS = XLSX.utils.aoa_to_sheet(toSheetData(ccRows));
    XLSX.utils.book_append_sheet(wb, ccWS, 'CC Payroll');

    XLSX.writeFile(wb, `payroll_${periodStart}_${periodEnd}.xlsx`);
  }

  const totalADP = results?.results.reduce((s, r) => s + parseFloat(r.adpAmount || 0), 0) || 0;
  const totalCash = results?.results.reduce((s, r) => s + parseFloat(r.cashAmount || 0), 0) || 0;
  const totalCC = results?.results.reduce((s, r) => s + parseFloat(r.ccAmount || 0), 0) || 0;

  return (
    <div>
      <div className="pr-form-section">
        <div className="pr-form-section-title">Pay Period</div>
        <div className="pr-form-row">
          <div className="pr-form-group">
            <label>Period Start</label>
            <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} />
          </div>
          <div className="pr-form-group">
            <label>Period End</label>
            <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} />
          </div>
        </div>
        <div className="pr-form-group">
          <label>Processed By</label>
          <input type="text" value={processedBy} onChange={e => setProcessedBy(e.target.value)} placeholder="Your name" />
        </div>
      </div>

      <div className="pr-form-section">
        <div className="pr-form-section-title">Upload ConnectTeam Timesheets</div>
        <div className="pr-form-row">
          <div className="pr-form-group">
            <label>Bakery / Farm / Store / Deliveries</label>
            <input type="file" accept=".xlsx,.csv" onChange={e => setBakeryFile(e.target.files[0])} />
            {bakeryFile && <small>✓ {bakeryFile.name}</small>}
          </div>
          <div className="pr-form-group">
            <label>Collective Culture</label>
            <input type="file" accept=".xlsx,.csv" onChange={e => setCCFile(e.target.files[0])} />
            {ccFile && <small>✓ {ccFile.name}</small>}
          </div>
        </div>
      </div>

      {error && <div className="pr-error">{error}</div>}

      <button className="pr-btn-primary" onClick={handleProcess} disabled={processing}>
        {processing ? 'Processing...' : '▶ Process Payroll'}
      </button>

      {results && (
        <div style={{marginTop: '32px'}}>
          {results.unrecognized?.length > 0 && (
            <div className="pr-unrecognized">
              <h4>⚠️ Unrecognized Employees — Please add to Employee Settings:</h4>
              <ul>{results.unrecognized.map(n => <li key={n}>{n}</li>)}</ul>
            </div>
          )}

          <div className="pr-summary-cards">
            <div className="pr-summary-card">
              <div className="pr-summary-label">Total ADP</div>
              <div className="pr-summary-value">${totalADP.toFixed(2)}</div>
            </div>
            <div className="pr-summary-card">
              <div className="pr-summary-label">Total Cash</div>
              <div className="pr-summary-value">${totalCash.toFixed(2)}</div>
            </div>
            <div className="pr-summary-card">
              <div className="pr-summary-label">CC Payroll Cost</div>
              <div className="pr-summary-value">${totalCC.toFixed(2)}</div>
            </div>
            <div className="pr-summary-card">
              <div className="pr-summary-label">Period</div>
              <div className="pr-summary-value" style={{fontSize:'0.9rem'}}>{periodStart} → {periodEnd}</div>
            </div>
          </div>

          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
            <h3 style={{margin:0, color:'#2c3e50'}}>Results</h3>
            <button className="pr-btn-primary" onClick={handleExport}>⬇ Export to Excel</button>
          </div>

          <table className="pr-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Dept</th>
                <th>Pay Type</th>
                <th>ADP Hrs</th>
                <th>ADP $</th>
                <th>Cash Hrs</th>
                <th>Cash $</th>
                <th>PTO Hrs</th>
                <th>CC Cost</th>
              </tr>
            </thead>
            <tbody>
              {results.results.map((r, i) => (
                <tr key={i}>
                  <td className="pr-td-name">{r.firstName} {r.lastName}</td>
                  <td><span className={`pr-dept-badge pr-dept-${r.department?.toLowerCase()}`}>{r.department}</span></td>
                  <td style={{fontSize:'0.8rem'}}>{r.payType}</td>
                  <td>{r.adpHours}</td>
                  <td>${r.adpAmount}</td>
                  <td>{r.cashHours}</td>
                  <td>${r.cashAmount}</td>
                  <td>{r.ptoHours}</td>
                  <td>{parseFloat(r.ccAmount) > 0 ? `$${r.ccAmount}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    {runHistory.length > 0 && (
        <div style={{marginTop:'40px'}}>
          <h3 style={{color:'#2c3e50', marginBottom:'12px'}}>📋 Payroll Run History</h3>
          <table className="pr-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Processed</th>
                <th>By</th>
                <th>Total ADP</th>
                <th>Total Cash</th>
                <th>CC Payroll</th>
              </tr>
            </thead>
            <tbody>
              {runHistory.map(run => (
                <tr key={run.id}>
                  <td>{run.periodStart ? String(run.periodStart).slice(0,10) : '—'} → {run.periodEnd ? String(run.periodEnd).slice(0,10) : '—'}</td>
                  <td>{run.processedDate ? String(run.processedDate).slice(0,10) : '—'}</td>
                  <td>{run.processedBy}</td>
                  <td>${parseFloat(run.totalADP || 0).toFixed(2)}</td>
                  <td>${parseFloat(run.totalCash || 0).toFixed(2)}</td>
                  <td>${parseFloat(run.totalCC || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
